import { useState, useEffect, type ReactNode } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { IdCardIcon, BuildingIcon } from 'lucide-react';
import type { SimpleAccount } from '@/lib/types';
import { AccountAppSettings } from './app-settings';
import { LogisticsShippingKpi } from './LogisticsShippingKpi';
import { InterStoreTransferAnalysis } from './InterStoreTransferAnalysis';
import { WarehouseList } from './WarehouseList';
import { SellerList } from './SellerList';
import { WarehouseSellerMappingList } from './WarehouseSellerMappingList';
import { SellerGroupList } from './SellerGroupList';
import { ProductGroupTreeList } from './ProductGroupTreeList';
import { SellerHierarchyList } from './SellerHierarchyList';
import { EcSellerSetting } from './EcSellerSetting';
import { PotentialAnalysisSetting } from './PotentialAnalysisSetting';
import { SellerTagManagement } from './seller-tag/SellerTagManagement';
import { NewProductList } from './NewProductList';
import {
  AnalysisDataExtraction,
  DailyStoreSkuExtraction,
  ProductMasterExtraction,
  StoreSkuSalesDatesExtraction,
  StoreSkuSettingValuesExtraction,
} from './analysis-data-extraction';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `account-tab-${index}`,
    'aria-controls': `account-tabpanel-${index}`,
  };
}

interface AccountTabsSimpleProps {
  account: SimpleAccount;
}

/** タブの最大インデックス。タブを増やしたらここも更新する（前回タブの復元判定に使う） */
const MAX_TAB_INDEX = 17;

export function AccountTabsSimple({ account }: AccountTabsSimpleProps) {
  // ローカルストレージキーをアカウントIDを含めて一意にする
  // AIクエリタブ削除でインデックスが繰り下がり、旧キー（account-tab-{id}）の保存値は別タブを指しうるため、キーを変えて読まないようにする
  const storageKey = `account-tab-v2-${account.id}`;

  // ローカルストレージから保存されたタブ値を取得（初期値は0）
  const getInitialTabValue = (): number => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsedValue = parseInt(saved, 10);
        // 有効な範囲内のタブ値かチェック（タブを増やしたらここも更新する）
        return parsedValue >= 0 && parsedValue <= MAX_TAB_INDEX ? parsedValue : 0;
      }
    }
    return 0;
  };

  const [tabValue, setTabValue] = useState<number>(getInitialTabValue);

  // タブ変更時にローカルストレージに保存
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (typeof window !== 'undefined') {
      localStorage.setItem(storageKey, newValue.toString());
    }
  };

  // コンポーネントマウント時にローカルストレージから値を復元
  useEffect(() => {
    const savedValue = getInitialTabValue();
    if (savedValue !== tabValue) {
      setTabValue(savedValue);
    }
  }, [account.id]); // アカウントIDが変わったら再実行

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="アカウント情報タブ"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': {
                opacity: 0.3,
              },
            },
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="基本情報" {...a11yProps(0)} />
          <Tab label="倉庫出荷効果検証" {...a11yProps(1)} />
          <Tab label="店間移動効果検証" {...a11yProps(2)} />
          <Tab label="倉庫一覧" {...a11yProps(3)} />
          <Tab label="店舗グループ一覧" {...a11yProps(4)} />
          <Tab label="店舗一覧" {...a11yProps(5)} />
          <Tab label="商品階層一覧" {...a11yProps(6)} />
          <Tab label="新商品一覧" {...a11yProps(7)} />
          <Tab label="店舗倉庫マッピング一覧" {...a11yProps(8)} />
          <Tab label="[週次/月次] 店舗×商品グループ実績抽出" {...a11yProps(9)} />
          <Tab label="[日別] 店舗×SKU 予測/実績/在庫抽出" {...a11yProps(10)} />
          <Tab label="商品マスタ抽出" {...a11yProps(11)} />
          <Tab label="店舗階層抽出" {...a11yProps(12)} />
          <Tab label="EC店舗設定" {...a11yProps(13)} />
          <Tab label="店舗×SKU 設定値リスト" {...a11yProps(14)} />
          <Tab label="店舗×SKU 初回/最終販売日・初回入庫日" {...a11yProps(15)} />
          <Tab label="店舗タグ管理" {...a11yProps(16)} />
          <Tab label="ポテンシャル分析設定" {...a11yProps(17)} />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                基本情報
              </Typography>
              <List>
                <ListItem>
                  <IdCardIcon size={20} style={{ marginRight: 8 }} />
                  <ListItemText primary="アカウントID" secondary={account.id} />
                </ListItem>
                <ListItem>
                  <BuildingIcon size={20} style={{ marginRight: 8 }} />
                  <ListItemText primary="アカウントコード" secondary={account.code} />
                </ListItem>
                <ListItem>
                  <BuildingIcon size={20} style={{ marginRight: 8 }} />
                  <ListItemText primary="会社名" secondary={account.name} />
                </ListItem>
                <ListItem>
                  <IdCardIcon size={20} style={{ marginRight: 8 }} />
                  <ListItemText
                    primary="基準日"
                    secondary={
                      account.base_date
                        ? new Date(account.base_date).toLocaleDateString('ja-JP')
                        : '未設定'
                    }
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
          {/* アカウント切替で前アカウントの値が残らないよう key でアンマウントさせる */}
          <AccountAppSettings key={account.code} account={account} />
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <LogisticsShippingKpi account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <InterStoreTransferAnalysis account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <WarehouseList account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <SellerGroupList account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <SellerList account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={6}>
        <ProductGroupTreeList account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={7}>
        <NewProductList account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={8}>
        <WarehouseSellerMappingList account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={9}>
        <AnalysisDataExtraction account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={10}>
        <DailyStoreSkuExtraction account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={11}>
        <ProductMasterExtraction account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={12}>
        <SellerHierarchyList account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={13}>
        <EcSellerSetting account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={14}>
        <StoreSkuSettingValuesExtraction account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={15}>
        <StoreSkuSalesDatesExtraction account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={16}>
        <SellerTagManagement account={account} />
      </TabPanel>

      <TabPanel value={tabValue} index={17}>
        <PotentialAnalysisSetting account={account} />
      </TabPanel>
    </Box>
  );
}
